import { Injectable, computed, inject, signal } from '@angular/core';
import { catchError, finalize, forkJoin, of } from 'rxjs';
import { AuthService } from './auth.service';
import { SalaService } from './sala.service';
import { CustomerService } from './customer.service';
import { ReservationService } from './reservation.service';
import { BusinessConfigService } from './business-config.service';
import { BusinessSiteInfo, Espacio, Reservation } from '../models/sala.model';

/** Rango por defecto de la agenda; se amplía si una reserva real cae fuera. */
const AGENDA_DEFAULT_START_MIN = 8 * 60;
const AGENDA_DEFAULT_END_MIN = 22 * 60;
/** Tope de horas dibujadas: una jornada completa en una fila no es usable. */
const AGENDA_MAX_HOURS = 18;

export type StatKey = 'reservations' | 'spaces' | 'customers' | 'payments';

/** Bloque de la agenda ya normalizado y ubicado en su carril. */
export interface AgendaBlock {
  reservation: Reservation;
  /** Minutos desde medianoche (recortados a la ventana visible). */
  startMin: number;
  endMin: number;
  lane: number;
  lanes: number;
}

export interface AgendaRow {
  space: Espacio;
  blocks: AgendaBlock[];
}

export interface PendingTask {
  key: string;
  icon: string;
  tone: 'orange' | 'violet' | 'green' | 'blue';
  title: string;
  detail: string;
  module: string;
}

export interface DashboardTip {
  text: string;
  action: string;
  module: string;
}

/** Rotación semanal estable: durante un mismo día el consejo no cambia. */
const DAILY_TIPS: DashboardTip[] = [
  { text: 'Mantén actualizadas las fotografías de tus espacios: una portada clara y luminosa ayuda a que los clientes elijan con confianza.', action: 'Revisar espacios', module: 'salas' },
  { text: 'Revisa los horarios disponibles al comenzar la semana para evitar solicitudes fuera de tu jornada de atención.', action: 'Ver calendario', module: 'calendario' },
  { text: 'Comprueba que el aforo, la tarifa y las características de cada espacio sigan vigentes.', action: 'Gestionar espacios', module: 'salas' },
  { text: 'Da seguimiento a las reservas pendientes para que cada cliente reciba una respuesta a tiempo.', action: 'Ver reservas', module: 'reservas' },
  { text: 'Una descripción breve y concreta ayuda a destacar qué hace especial a cada sala u oficina.', action: 'Mejorar espacios', module: 'salas' },
  { text: 'Verifica las próximas reservas y prepara con anticipación el espacio, el acceso y el equipo solicitado.', action: 'Abrir calendario', module: 'calendario' },
  { text: 'Mantén ordenadas tus reservas y pagos pendientes para comenzar la siguiente semana con todo al día.', action: 'Revisar reservas', module: 'reservas' },
];

/** Fecha local (no UTC) en YYYY-MM-DD, como el resto del panel. */
function toIso(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function toMinutes(time: string | null | undefined): number {
  if (!time) return 0;
  const [h, m] = time.split(':').map(Number);
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
}

/** Fin del bloque: si termina antes de empezar, la reserva cruza medianoche. */
function blockEnd(startMin: number, endMin: number): number {
  return endMin <= startMin ? endMin + 24 * 60 : endMin;
}

function reservationDay(reservation: Reservation): string {
  return (reservation.reservation_date || reservation.date || '').slice(0, 10);
}

function monthKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth() + 1}`;
}

function nextMonthDate(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 1);
}

/**
 * Datos del panel de inicio (centro operativo, no módulo de estadísticas).
 *
 * Todo proviene de los servicios reales del negocio; el backend ya aplica el
 * aislamiento multi-tenant y los permisos, aquí solo se reúne y se deriva. Las
 * reservas mensuales se cachean por mes y en una señal, de modo que la agenda
 * y los contadores del día comparten una única petición y cambiar de fecha
 * dentro del mismo mes no vuelve a consultar.
 */
@Injectable({ providedIn: 'root' })
export class DashboardService {
  private readonly auth = inject(AuthService);
  private readonly salaService = inject(SalaService);
  private readonly customerService = inject(CustomerService);
  private readonly reservationService = inject(ReservationService);
  private readonly businessConfig = inject(BusinessConfigService);

  readonly today = signal(toIso(new Date()));
  readonly selectedDate = signal(toIso(new Date()));

  /** Catálogo completo del dashboard. No reutiliza la página de 12 registros
   * de Gestión de salas porque eso produciría contadores parciales. */
  readonly spaces = signal<Espacio[]>([]);
  readonly spacesLoading = signal(false);
  readonly spacesError = signal<string | null>(null);
  readonly spacesTotal = computed(() => this.spaces().length);

  readonly customersTotal = signal<number | null>(null);
  readonly pendingPayments = signal<number | null>(null);
  readonly pendingRequests = signal<number | null>(null);
  readonly site = signal<BusinessSiteInfo | null>(null);

  readonly calendarLoading = signal(false);
  readonly calendarError = signal(false);
  readonly statsLoading = signal(false);
  readonly loaded = signal(false);
  /** Resumen mínimo listo (ver loadSummary): lo que usa Kubo fuera del inicio. */
  readonly summaryLoaded = signal(false);

  /** Reservas por mes, cacheado y reactivo (clave `YYYY-M`). */
  private readonly reservationsByMonth = signal<Record<string, Reservation[]>>({});
  /** Tokens independientes: un contador compartido daba por obsoleta la
   *  respuesta de un grupo al pedir el mes siguiente y dejaba el estado
   *  de carga encendido. */
  private statsToken = 0;
  private monthToken = 0;
  private readonly businessId = computed(() => this.auth.currentUser()?.id ?? 0);

  // ---- Derivados -----------------------------------------------------------

  readonly activeSpaces = computed(() =>
    this.spaces().filter(s => s.is_active && s.status !== 'under_maintenance')
  );

  /** Espacios operativos a los que les falta foto o tarifa. */
  readonly spacesNeedingAttention = computed(() =>
    this.spaces().filter(s => s.is_active && (!s.images?.length || !Number(s.price_per_hour)))
  );

  private readonly selectedMonthReservations = computed(
    () => this.reservationsByMonth()[monthKey(new Date(`${this.selectedDate()}T00:00:00`))] ?? null
  );

  /** true cuando el mes de la fecha seleccionada ya está en memoria. */
  readonly agendaReady = computed(() => this.selectedMonthReservations() !== null);

  readonly selectedDayReservations = computed(() =>
    (this.selectedMonthReservations() ?? []).filter(
      r => reservationDay(r) === this.selectedDate() && r.status !== 'cancelled'
    )
  );

  /** Reservas de hoy y de ayer (null si el mes actual no está cargado). */
  readonly dayCounts = computed<{ today: number; yesterday: number } | null>(() => {
    const data = this.reservationsByMonth()[monthKey(new Date())];
    if (!data) return null;
    const today = this.today();
    const yesterday = toIso(new Date(Date.now() - 86400000));
    const count = (day: string) =>
      data.filter(r => reservationDay(r) === day && r.status !== 'cancelled').length;
    return { today: count(today), yesterday: count(yesterday) };
  });

  /** Primera reserva cuya franja todavía no ha terminado (hoy en adelante). */
  readonly nextReservation = computed<Reservation | null>(() => {
    const now = new Date();
    const nowIso = toIso(now);
    const nowMin = now.getHours() * 60 + now.getMinutes();
    const months = this.reservationsByMonth();
    const candidates = [
      ...(months[monthKey(now)] ?? []),
      ...(months[monthKey(nextMonthDate(now))] ?? []),
    ].filter(r => r.status !== 'cancelled');

    const upcoming = candidates
      .filter(r => {
        const day = reservationDay(r);
        if (day > nowIso) return true;
        if (day < nowIso) return false;
        return blockEnd(toMinutes(r.start_time), toMinutes(r.end_time)) > nowMin;
      })
      .sort((a, b) => {
        const da = reservationDay(a);
        const db = reservationDay(b);
        if (da !== db) return da < db ? -1 : 1;
        return toMinutes(a.start_time) - toMinutes(b.start_time);
      });

    return upcoming[0] ?? null;
  });

  /** Ventana horaria que cubre la agenda del día seleccionado. */
  readonly agendaWindow = computed(() => {
    let start = AGENDA_DEFAULT_START_MIN;
    let end = AGENDA_DEFAULT_END_MIN;
    for (const r of this.selectedDayReservations()) {
      const s = toMinutes(r.start_time);
      const e = blockEnd(s, toMinutes(r.end_time));
      if (s < start) start = s;
      if (e > end) end = e;
    }
    start = Math.max(0, Math.floor(start / 60) * 60);
    end = Math.min(24 * 60, Math.ceil(end / 60) * 60);
    if (end - start > AGENDA_MAX_HOURS * 60) end = start + AGENDA_MAX_HOURS * 60;
    return { start, end };
  });

  /** Filas de la agenda: cada espacio con sus bloques ya repartidos en carriles. */
  readonly agendaRows = computed<AgendaRow[]>(() => {
    const { start, end } = this.agendaWindow();
    const bySpace = new Map<number, AgendaBlock[]>();
    for (const r of this.selectedDayReservations()) {
      const s = toMinutes(r.start_time);
      const e = blockEnd(s, toMinutes(r.end_time));
      if (e <= start || s >= end) continue;
      const list = bySpace.get(r.space_id) ?? [];
      list.push({
        reservation: r,
        startMin: Math.max(s, start),
        endMin: Math.min(e, end),
        lane: 0,
        lanes: 1,
      });
      bySpace.set(r.space_id, list);
    }
    return this.spaces()
      .map(space => ({ space, blocks: assignLanes(bySpace.get(space.space_id) ?? []) }))
      // Una reserva existente siempre se conserva. Sin reserva, solo se
      // dibujan espacios realmente operativos: mantenimiento no equivale a
      // disponibilidad aunque is_active siga siendo true.
      .filter(row => row.blocks.length > 0 || (row.space.is_active && row.space.status !== 'under_maintenance'));
  });

  readonly tasks = computed<PendingTask[]>(() => {
    const list: PendingTask[] = [];
    const payments = this.pendingPayments() ?? 0;
    const requests = this.pendingRequests() ?? 0;
    const incomplete = this.spacesNeedingAttention().length;
    if (payments > 0) {
      list.push({
        key: 'payments', icon: 'bi-cash-coin', tone: 'orange',
        title: payments === 1 ? '1 pago pendiente de confirmar' : `${payments} pagos pendientes de confirmar`,
        detail: 'Reservas con cobro por confirmar', module: 'reservas',
      });
    }
    if (requests > 0) {
      list.push({
        key: 'requests', icon: 'bi-inbox', tone: 'violet',
        title: requests === 1 ? '1 solicitud por revisar' : `${requests} solicitudes por revisar`,
        detail: 'Nuevas reservas recibidas', module: 'reservas',
      });
    }
    if (incomplete > 0) {
      list.push({
        key: 'spaces', icon: 'bi-image', tone: 'green',
        title: incomplete === 1 ? 'Actualizar un espacio' : `Actualizar ${incomplete} espacios`,
        detail: 'Sin foto de portada o sin tarifa definida', module: 'salas',
      });
    }
    return list;
  });

  readonly tip = computed<DashboardTip>(() => {
    const localDay = new Date(`${this.today()}T12:00:00`).getDay();
    return DAILY_TIPS[localDay] ?? DAILY_TIPS[0];
  });

  /** Espacios con al menos una reserva activa hoy (para la etiqueta de estado). */
  readonly todayBusySpaceIds = computed(() => {
    const data = this.reservationsByMonth()[monthKey(new Date())] ?? [];
    const today = this.today();
    return new Set(
      data
        .filter(r => reservationDay(r) === today && r.status !== 'cancelled')
        .map(r => r.space_id)
    );
  });

  /** Banner institucional común para todos los perfiles administrativos. */
  readonly heroImage = computed<string>(() => 'dashboard-assets/banner-admin-tusala.png');

  /** Ubicación mostrada en el widget del banner (dirección o ciudad). */
  readonly location = computed<string>(() => {
    const address = this.site()?.address?.trim();
    if (address) return address;
    const space = this.spaces().find(s => s.city || s.country);
    return space ? [space.city, space.country].filter(Boolean).join(', ') : '';
  });

  // ---- Carga --------------------------------------------------------------

  /** Carga (o recarga) del panel completo. */
  load(): void {
    const id = this.businessId();
    if (!id) return;
    const token = ++this.statsToken;
    const canSpaces = this.auth.hasPermission('spaces');
    const canReservations = this.auth.hasPermission('reservations');
    const canCustomers = this.auth.hasPermission('customers');

    this.statsLoading.set(true);
    if (canSpaces) this.loadSpaces();

    forkJoin({
      customers: canCustomers
        ? this.customerService.getClientesPaginados(id, 1, 1).pipe(catchError(() => of(null)))
        : of(null),
      payments: canReservations
        ? this.reservationService
            .getAdminReservations({ payment_status: 'pending', limit: 1 })
            .pipe(catchError(() => of(null)))
        : of(null),
      requests: canReservations
        ? this.reservationService
            .getAdminReservations({ status: 'pending', limit: 1 })
            .pipe(catchError(() => of(null)))
        : of(null),
    })
      .pipe(finalize(() => { if (token === this.statsToken) this.statsLoading.set(false); }))
      .subscribe(results => {
        if (token !== this.statsToken) return;
        this.customersTotal.set(results.customers?.total_customers ?? null);
        this.pendingPayments.set(results.payments?.count ?? null);
        this.pendingRequests.set(results.requests?.count ?? null);
        this.loaded.set(true);
      });

    this.loadSite(id);
    if (canReservations) {
      this.loadMonth(new Date(`${this.selectedDate()}T00:00:00`));
      // El "próximo" puede caer en el mes siguiente: se precarga en silencio.
      this.loadMonth(nextMonthDate(new Date()), true);
    }
  }

  /**
   * Lo mínimo que necesita Kubo para sus avisos (espacios y pendientes de
   * reservas/pagos), sin agenda ni clientes: 3 consultas en vez de la carga
   * completa del inicio. Si el inicio ya cargó (o está cargando), no hace nada
   * salvo que se fuerce (después de guardar algo).
   */
  loadSummary(force = false): void {
    const id = this.businessId();
    if (!id || this.statsLoading() || (!force && (this.loaded() || this.summaryLoaded()))) return;
    if (this.auth.hasPermission('spaces') && !this.spacesLoading()) this.loadSpaces();
    if (!this.auth.hasPermission('reservations')) { this.summaryLoaded.set(true); return; }
    forkJoin({
      payments: this.reservationService.getAdminReservations({ payment_status: 'pending', limit: 1 }).pipe(catchError(() => of(null))),
      requests: this.reservationService.getAdminReservations({ status: 'pending', limit: 1 }).pipe(catchError(() => of(null))),
    }).subscribe(results => {
      this.pendingPayments.set(results.payments?.count ?? null);
      this.pendingRequests.set(results.requests?.count ?? null);
      this.summaryLoaded.set(true);
    });
  }

  /** Cambia el día de la agenda (solo consulta si cambia de mes). */
  selectDate(iso: string): void {
    this.selectedDate.set(iso);
    this.loadMonth(new Date(`${iso}T00:00:00`));
  }

  /** Reintenta la carga del mes de la agenda. */
  reloadAgenda(): void {
    const date = new Date(`${this.selectedDate()}T00:00:00`);
    this.reservationsByMonth.update(cache => {
      const next = { ...cache };
      delete next[monthKey(date)];
      return next;
    });
    this.loadMonth(date);
  }

  reloadSpaces(): void {
    this.loadSpaces();
  }

  private loadSpaces(): void {
    this.spacesLoading.set(true);
    this.spacesError.set(null);
    this.salaService.obtenerEspaciosParaSelector()
      .pipe(finalize(() => this.spacesLoading.set(false)))
      .subscribe({
        next: spaces => this.spaces.set(spaces),
        error: () => {
          this.spaces.set([]);
          this.spacesError.set('No fue posible cargar los espacios.');
        },
      });
  }

  private loadSite(id: number): void {
    this.businessConfig
      .obtenerSitio(id)
      .pipe(catchError(() => of(null)))
      .subscribe(site => this.site.set(site));
  }

  private loadMonth(date: Date, silent = false): void {
    const key = monthKey(date);
    if (this.reservationsByMonth()[key]) return;

    // La precarga en silencio (mes siguiente, usado para calcular la próxima
    // reserva) no debe invalidar el token de la carga visible de la agenda.
    const store = (response: { reservations?: Reservation[] } | null) =>
      this.reservationsByMonth.update(cache => ({ ...cache, [key]: response?.reservations ?? [] }));

    if (silent) {
      this.salaService
        .getCalendarData(date.getMonth() + 1, date.getFullYear())
        .pipe(catchError(() => of(null)))
        .subscribe(response => store(response));
      return;
    }

    const token = ++this.monthToken;
    this.calendarLoading.set(true);
    this.calendarError.set(false);
    this.salaService
      .getCalendarData(date.getMonth() + 1, date.getFullYear())
      .pipe(finalize(() => { if (token === this.monthToken) this.calendarLoading.set(false); }))
      .subscribe({
        next: response => store(response),
        error: () => { if (token === this.monthToken) this.calendarError.set(true); },
      });
  }
}

/**
 * Reparte los bloques de una fila en carriles para que las reservas
 * superpuestas se vean todas (una al lado de otra) en lugar de ocultarse.
 */
function assignLanes(blocks: AgendaBlock[]): AgendaBlock[] {
  const sorted = [...blocks].sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);
  const laneEnds: number[] = [];
  for (const block of sorted) {
    let lane = laneEnds.findIndex(end => end <= block.startMin);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(0);
    }
    laneEnds[lane] = block.endMin;
    block.lane = lane;
  }
  const lanes = Math.max(1, laneEnds.length);
  for (const block of sorted) block.lanes = lanes;
  return sorted;
}
